import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { ApiResponse } from "src/app/core/models/api.model";
import { DataService } from "./dataservices/data.service";
import { SKIP_SESSION_LOGOUT_ON_401 } from "../interceptors/auth.interceptor";

interface RealtimeTokenResult {
  token?: string;
  expiresIn?: number;
}

/**
 * Mints and caches the short-lived realtime JWT the notification socket
 * presents to flymsg-node's `/notifications` namespace (issue #416).
 *
 * As of issue #582 (AI-27), flymsg-node mints its own realtime tokens
 * (`POST /api/v3/realtime/token`, AI-31 — verified against
 * `realtimeTokenController.ts`/`realtimeTokenMint.ts`): scopes are derived
 * SERVER-SIDE from the caller's live `feature_releases` grants, never sent
 * by the client, so the request body is empty. Node answers `404 {"error":
 * "Not Found"}` (an envelope-bypassing `RawBodyError`, deliberately not a
 * 403 — "a dark feature should look like it doesn't exist") whenever the
 * caller has no grantable realtime scope. This service tries node FIRST and
 * falls back to the legacy flymsg-be mint (`POST v2/realtime/token`, which
 * only ever grants `scopes:["copilot"]`) on ANY failure — a 404 (no scope
 * yet, or route not wired in this environment), a network error, a 401
 * (node's independent bearer validation rejecting a token Laravel still
 * accepts mid-rollout), or a 2xx with a malformed/missing `result.token`
 * body. Neither path is guaranteed to hand back a
 * `"notifications"` scope today; `NotificationSocketService` already treats
 * a missing/rejected token as any other unreachable-socket case and falls
 * back to polling without surfacing an error to the user.
 */
@Injectable({ providedIn: "root" })
export class NotificationRealtimeTokenService {
  /** Re-mint once the cached token has less than this much TTL left. */
  private static readonly TOKEN_REFRESH_SKEW_MS = 60_000;
  /** Conservative fallback TTL when the server omits a usable `expiresIn`. */
  private static readonly DEFAULT_TOKEN_TTL_MS = 5 * 60_000;

  private cachedToken: string | null = null;
  private expiresAtMs = 0;
  /** Shared in-flight mint so a burst of callers triggers exactly one POST. */
  private inFlight: Promise<string> | null = null;

  constructor(
    private readonly dataService: DataService,
    private readonly http: HttpClient
  ) {}

  /**
   * Returns a valid realtime JWT, minting a new one when the cache is empty
   * or close to expiry. Concurrent callers share a single in-flight request.
   */
  getRealtimeToken(): Promise<string> {
    const skew = NotificationRealtimeTokenService.TOKEN_REFRESH_SKEW_MS;
    if (this.cachedToken && Date.now() < this.expiresAtMs - skew) {
      return Promise.resolve(this.cachedToken);
    }
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.mintToken().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  /** Drops the cached token so the next {@link getRealtimeToken} re-mints. */
  invalidate(): void {
    this.cachedToken = null;
    this.expiresAtMs = 0;
  }

  private async mintToken(): Promise<string> {
    let response: ApiResponse<RealtimeTokenResult> | null = null;
    try {
      response = await this.mintFromNode();
      // A node 2xx with no usable `result.token` (malformed/empty body) is
      // treated the same as a thrown failure below — the check runs INSIDE
      // the try so it falls through to the legacy mint too, rather than
      // escaping the catch and short-circuiting the fallback entirely
      // (issue #582/AI-27 fix-round F1: QA reproduced this exact mode not
      // falling back).
      if (!response?.result?.token) {
        throw new Error("Realtime token endpoint returned no token");
      }
    } catch {
      // Node 404 (no grantable scope / route not yet wired here), a network
      // error, a 401 (SKIP_SESSION_LOGOUT_ON_401 keeps this from ever
      // cascading into a Laravel refresh/logout), or a 2xx with a malformed/
      // missing token body — fall back to the legacy mint during the
      // transition. Feature-detected per call, never cached: a caller who
      // gains the grant later picks up node on their very next (post-expiry)
      // mint.
      response = await this.mintFromLegacy();
    }

    const token = response?.result?.token;
    if (!token) {
      throw new Error("Realtime token endpoint returned no token");
    }

    const expiresIn = response.result?.expiresIn;
    const ttlMs =
      typeof expiresIn === "number" && Number.isFinite(expiresIn) && expiresIn > 0
        ? expiresIn * 1000
        : NotificationRealtimeTokenService.DEFAULT_TOKEN_TTL_MS;
    this.cachedToken = token;
    this.expiresAtMs = Date.now() + ttlMs;
    return token;
  }

  /**
   * `POST /api/v3/realtime/token` — no request body: scopes are derived
   * server-side from the caller's live feature grants, never client-
   * supplied (verified against `realtimeTokenMint.ts`). Carries
   * `SKIP_SESSION_LOGOUT_ON_401` since node validates the bearer
   * independently of Laravel — a node-only rejection must never eject the
   * whole FlyMSG session.
   */
  private mintFromNode(): Promise<ApiResponse<RealtimeTokenResult>> {
    return firstValueFrom(
      this.http.post<ApiResponse<RealtimeTokenResult>>(
        `${environment.NODE_API_URL}/api/v3/realtime/token`,
        {},
        { context: new HttpContext().set(SKIP_SESSION_LOGOUT_ON_401, true) }
      )
    );
  }

  /** The retired Laravel mint (`scopes:["copilot"]` only) — the fallback path during the node transition. */
  private mintFromLegacy(): Promise<ApiResponse<RealtimeTokenResult>> {
    return firstValueFrom(
      this.dataService.post<ApiResponse<RealtimeTokenResult>>({
        url: "v2/realtime/token",
        data: {},
        isLoader: false,
        ignoreErrors: true,
      })
    );
  }
}
